import { AxiosPromise } from 'axios';
import { ResponseValues } from 'axios-hooks';

import type { FileMapping } from './common';

export interface SerializedFilePayload {
  filename: string;
  uuid: string;
  size: number;
}

/**
 * response from an object
 */
export interface Response<T, TBody> extends Omit<ResponseValues<T, TBody, Error>, 'response'> {
  /**
   * refetch request to the api URL
   */
  refetch: () => AxiosPromise;
}
export interface Voucher {
  voucherCode: string;
  childName?: string;
  activity: string;
  amount: string;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  country: string;
  postcode: string;
  addressValidated: boolean;
}

interface Organisation {
  name: string;
  activityType: string;
  phone: string;
  address: Address;
}

interface AuthorisedContact {
  idamIdToken?: string;
  role: string;
}

export interface ProviderDetailsRequest {
  transactionId: string;
  providerId: string;
  organisation: Organisation;
  authorisedContact: AuthorisedContact;
}

export enum VoucherErrorType {
  DUPLICATE_VOUCHER = 'DUPLICATE_VOUCHER',
  VOUCHER_NOT_FOUND = 'VOUCHER_NOT_FOUND',
  VOUCHER_REDEEMED = 'VOUCHER_REDEEMED',
  VOUCHER_CANCELLED = 'VOUCHER_CANCELLED',
  VOUCHER_EXPIRED = 'VOUCHER_EXPIRED',
  INVALID_AMOUNT = 'INVALID_AMOUNT',
  INVALID_PROVIDER = 'INVALID_PROVIDER',
  USER_UNAUTHORIZED = 'USER_UNAUTHORIZED',
  EMPTY_RECORD = 'EMPTY_RECORD',
  SERVER_ERROR = 'SERVER_ERROR'
}

export enum CSVErrorType {
  CSV_NOT_FOUND = 'CSV_NOT_FOUND',
  CSV_INVALID = 'CSV_INVALID',
  CSV_EMPTY = 'CSV_EMPTY',
  TOO_MANY_RECORDS = 'TOO_MANY_RECORDS'
}

export interface FetchProviderResponse {
  orgName: string;
  providerId: string;
}

export type FetchProvidersResponse = FetchProviderResponse[];

export interface ProviderDetailResponse {
  id: string;
  code: string;
  organisation: {
    name: string;
    activityType: string;
    address: Address;
    phone: string;
  };
  authorisedContact: {
    role: string;
  };
}

interface BaseVoucherRedemptionRequest {
  transactionId: string;
  idamIdToken: string;
  validateOnly: boolean;
}

interface BaseVoucherResult {
  voucherCode: string;
  success: boolean;
  orgName?: string;
  errorType?: VoucherErrorType;
}

export interface IndividualVoucherResult extends BaseVoucherResult {}

export interface BulkVoucherResult extends BaseVoucherResult {
  row: number;
  activityType?: string;
  amount?: string;
}

export interface IndividualVoucherRedemptionRequest extends BaseVoucherRedemptionRequest {
  vouchers: Voucher[];
}

export interface BulkVoucherRedemptionRequest extends BaseVoucherRedemptionRequest {
  csv: FileMapping;
}

interface BaseRedemptionResponse {
  transactionId: string;
  success: boolean;
}

export interface VoucherRedemptionResponse extends BaseRedemptionResponse {
  vouchers: IndividualVoucherResult[];
}

export interface BulkVoucherRedemptionResponse extends BaseRedemptionResponse {
  errorMessage?: string;
  vouchers?: BulkVoucherResult[];
  success: boolean;
}
